import { useState, useRef } from "react";
//import { supabase } from "./supabase";
import { useUser } from "@clerk/clerk-react";

const C = {
  bg: '#020617', surface: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.10)',
  accent: '#60A5FA', gold: '#F0A500', green: '#34D399',
  red: '#F87171', text: '#FFFFFF', textMuted: '#94A3B8',
  accentGlow: 'rgba(96,165,250,0.15)',
};

export function VideoUpload({ onUploadComplete }) {
  const { user } = useUser();
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [videoData, setVideoData] = useState(null);
  const fileRef = useRef();

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("File too large. Maximum size is 2GB.");
      return;
    }

    if (!file.type.startsWith("video/")) {
      setError("Please select a video file (MP4, MOV, etc.)");
      return;
    }

    setStatus("preparing");
    setError(null);

    try {
      const res = await fetch("/api/mux-upload", { method: "POST" });
      const { uploadId, uploadUrl } = await res.json();

      setStatus("uploading");

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        });
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed: ${xhr.status}`));
        });
        xhr.addEventListener("error", reject);
        xhr.open("PUT", uploadUrl);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.send(file);
      });

      setStatus("processing");
      setProgress(100);

      const pollInterval = setInterval(async () => {
        const pollRes = await fetch(`/api/mux-upload?uploadId=${uploadId}`);
        const { status: uploadStatus, assetId } = await pollRes.json();

        if (uploadStatus === "asset_created" && assetId) {
          clearInterval(pollInterval);

          const assetRes = await fetch(`/api/mux-asset?assetId=${assetId}`);
          const asset = await assetRes.json();

          if (asset.playbackId) {
            const data = {
              mux_upload_id: uploadId,
              mux_asset_id: assetId,
              mux_playback_id: asset.playbackId,
              thumbnail_url: asset.thumbnailUrl,
              duration: asset.duration,
              status: "ready",
              creator_id: user?.id,
            };

            setVideoData(data);
            setStatus("ready");
            onUploadComplete && onUploadComplete(data);
          }
        }
      }, 3000);

      setTimeout(() => clearInterval(pollInterval), 300000);

    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message);
      setStatus("idle");
    }
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {status === "idle" && (
        <div
          onClick={() => fileRef.current?.click()}
          style={{ border: `2px dashed ${C.border}`, borderRadius: 12, padding: "40px 28px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
          onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
        >
          <div style={{ fontSize: 36, marginBottom: 10 }}>📹</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 6 }}>
            Click to upload your video
          </div>
          <div style={{ fontSize: 12, color: C.textMuted }}>
            MP4, MOV, AVI up to 2GB · AI auto-generates captions
          </div>
          <input ref={fileRef} type="file" accept="video/*" style={{ display: "none" }} onChange={handleFileSelect} />
        </div>
      )}

      {status === "preparing" && (
        <div style={{ padding: "32px", textAlign: "center" }}>
          <div style={{ fontSize: 13, color: C.textMuted }}>Preparing upload...</div>
        </div>
      )}

      {status === "uploading" && (
        <div style={{ padding: "24px", background: C.surface, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>Uploading video...</span>
            <span style={{ fontSize: 13, color: C.accent, fontWeight: 700 }}>{progress}%</span>
          </div>
          <div style={{ height: 6, background: "rgba(0,168,232,0.12)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: `linear-gradient(90deg,${C.accent},${C.green})`, borderRadius: 3, transition: "width 0.3s ease" }} />
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>Do not close this page</div>
        </div>
      )}

      {status === "processing" && (
        <div style={{ padding: "24px", background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 8, animation: "spin 2s linear infinite", display: "inline-block" }}>⚙️</div>
          <div style={{ fontSize: 13, color: C.text, fontWeight: 600, marginBottom: 4 }}>Processing your video...</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>This usually takes 1–3 minutes. You can leave this page.</div>
        </div>
      )}

      {status === "ready" && videoData && (
        <div style={{ padding: "20px", background: C.surface, borderRadius: 12, border: `1px solid ${C.green}40` }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            {videoData.thumbnail_url && (
              <img src={videoData.thumbnail_url} alt="thumbnail" style={{ width: 80, height: 48, objectFit: "cover", borderRadius: 6 }} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.green, marginBottom: 4 }}>✅ Video ready</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>
                Playback ID: {videoData.mux_playback_id?.slice(0, 16)}...
                {videoData.duration && ` · ${Math.round(videoData.duration)}s`}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: "12px 16px", background: "rgba(232,89,60,0.1)", border: `1px solid rgba(232,89,60,0.3)`, borderRadius: 10, marginTop: 10 }}>
          <div style={{ fontSize: 13, color: C.red }}>⚠️ {error}</div>
        </div>
      )}
    </div>
  );
}